import { Check, Lock } from "lucide-react";
import type { ChatColleague } from "@/lib/chatApi";
import type { ChatPoll } from "@/lib/chatPolls";
import { memberLabel } from "@/lib/chatFormat";

type Props = {
  poll: ChatPoll;
  selfId: number;
  colleagues: ChatColleague[];
  busy?: boolean;
  canClose?: boolean;
  onVote: (optionId: number) => void;
  onClose?: () => void;
};

export function ChatPollCard({
  poll,
  selfId,
  colleagues,
  busy = false,
  canClose = false,
  onVote,
  onClose,
}: Props) {
  const total = Math.max(0, poll.total);
  const labelFor = (id: number) => {
    if (id === selfId) return "Vous";
    const col = colleagues.find((c) => c.id === id);
    return memberLabel(col, col?.username || `#${id}`);
  };

  return (
    <div className="chat-poll-card min-w-[13.5rem] max-w-[18rem] py-0.5">
      <p className="text-[13px] font-semibold leading-snug text-[var(--chat-text)]">{poll.question}</p>
      <ul className="mt-2 grid gap-1.5">
        {poll.options.map((opt) => {
          const mine = poll.my_option_id === opt.id;
          const pct = total > 0 ? Math.round((opt.count / total) * 100) : 0;
          const names = opt.voter_ids.map(labelFor).filter(Boolean);
          return (
            <li key={opt.id}>
              <button
                type="button"
                disabled={poll.closed || busy}
                aria-pressed={mine}
                className={`chat-poll-opt relative w-full overflow-hidden rounded-xl border px-2.5 py-1.5 text-left transition ${
                  mine
                    ? "border-[var(--chat-accent)] bg-[var(--chat-accent-soft)]"
                    : "border-[var(--chat-border)] bg-[var(--chat-bg)]/55 hover:border-[var(--chat-accent)]/50"
                } ${poll.closed || busy ? "cursor-default" : "cursor-pointer"}`}
                onClick={() => {
                  if (poll.closed || busy) return;
                  onVote(opt.id);
                }}
              >
                <span
                  className="pointer-events-none absolute inset-y-0 left-0 bg-[var(--chat-accent)]/18"
                  style={{ width: `${pct}%` }}
                  aria-hidden
                />
                <span className="relative flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2 text-[12px] font-medium text-[var(--chat-text)]">
                    <span
                      className={`grid size-[1.15rem] shrink-0 place-items-center rounded-full border-[1.5px] ${
                        mine
                          ? "border-[var(--chat-accent-strong)] bg-[var(--chat-accent-strong)] text-white"
                          : "border-[var(--chat-muted)] bg-transparent"
                      }`}
                      aria-hidden
                    >
                      {mine ? <Check className="size-2.5" strokeWidth={3} /> : null}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </span>
                  <span className="shrink-0 font-mono text-[11px] tabular-nums text-[var(--chat-muted)]">
                    {opt.count}
                  </span>
                </span>
                {names.length > 0 ? (
                  <span className="relative mt-0.5 block truncate text-[10px] text-[var(--chat-muted)]">
                    {names.join(", ")}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-[var(--chat-muted)]">
        <span>
          {total} vote{total > 1 ? "s" : ""}
          {poll.closed ? " · Clôturé" : ""}
        </span>
        {canClose && !poll.closed && onClose ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 font-medium text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
            onClick={onClose}
          >
            <Lock className="size-3" />
            Clôturer
          </button>
        ) : null}
      </div>
    </div>
  );
}
